import {
  Box,
  Grid,
  LuxButton,
  LuxLoader,
  LuxSelectablePill,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import useGetGalleryImages from '@hooks/useGetGalleryImages';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { TabletBreakpoint } from '@utils/consts/breakpoints.consts';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import { useHistory } from 'react-router-dom';
import { GalleryImagesModal } from './GalleryImagesModal';
import type { KeyboardEvent } from 'react';
import { useMemo, useState } from 'react';
import { StepContainer } from '@components/Steps/Steps.styles';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import { formatGeneralAddress } from '@components/BookingDetails/utils';
import {
  FirstImage,
  MainImageWrapper,
  MobileFirstImage,
  RightImages,
  ViewAllButtonContainer,
} from './styles';
import {
  getCurrentClosingTime,
  getTodayOpeningHours,
  isOpenNow,
  openGoogleReviews,
  renderStars,
} from './utils';
import { DirectionsModal } from './DirectionsModal';
import { GoogleReview } from './PracticeCardPage';
import { useTranslation } from 'react-i18next';

interface Props {
  rating: number | undefined;
  reviews: GoogleReview;
}

export function GalleryCard({ rating, reviews }: Props) {
  const history = useHistory();
  const { Urls } = useUrlsContext();
  const { t } = useTranslation();
  const theme = useTheme();

  const practiceId = useOnlineBookingStore(state => state.practiceId);
  const practiceInfo = useOnlineBookingStore(state => state.generalInfo);
  const practiceConfig = useOnlineBookingStore(state => state.generalConfig);
  const placeId = useOnlineBookingStore(
    state => state.generalInfo?.googlePlaceId,
  );
  const colorConfig = useOnlineBookingStore(
    state => state.generalConfig?.customization,
  );

  const { data: dataGallery, isFetching } = useGetGalleryImages({ practiceId });

  const [openModal, setOpenModal] = useState(false);
  const [openDirectionsModal, setOpenDirectionsModal] =
    useState<boolean>(false);
  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);

  const images = useMemo(() => {
    return dataGallery?.gallery?.images ?? [];
  }, [dataGallery]);

  const isOpen = useMemo(
    () => (practiceInfo?.hours ? isOpenNow(practiceInfo.hours) : false),
    [practiceInfo?.hours],
  );

  const closingTime = useMemo(
    () =>
      practiceInfo?.hours ? getCurrentClosingTime(practiceInfo.hours) : null,
    [practiceInfo?.hours],
  );

  const todayHours = useMemo(
    () => (practiceInfo?.hours ? getTodayOpeningHours(practiceInfo.hours) : []),
    [practiceInfo?.hours],
  );

  const openGalleryModal = () => setOpenModal(true);

  const handleOpenGalleryKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openGalleryModal();
    }
  };

  /*  console.log('show', gallery?.showPracticeCardsGallery);*/

  return (
    <>
      {isMobile &&
        (isFetching ? (
          <LuxLoader />
        ) : (
          images?.length > 0 &&
          dataGallery?.showPracticeCardsGallery && (
            <MainImageWrapper>
              <MobileFirstImage src={images[0].url} alt={images[0].alt || ''} />
              <ViewAllButtonContainer>
                <LuxSelectablePill
                  iconStart="ImageIcon"
                  text={t('practiceCard.viewAllPhotos', {
                    n: images?.length || 0,
                  })}
                  customColor={
                    theme.palette.LUX.tokens.backgroundsGenericBackground2
                  }
                  selectable={false}
                  customBorderColor={theme.palette.LUX.tokens.whiteWhiteA100}
                  onClick={openGalleryModal}
                />
              </ViewAllButtonContainer>
            </MainImageWrapper>
          )
        ))}
      <StepContainer>
        <CardTitleWrapper
          borderColor={theme.palette.LUX.obTokens.bordersColorNeutralSecondary}
        >
          <LuxTypography variant="heading2">{practiceInfo?.name}</LuxTypography>
        </CardTitleWrapper>
        <Stack padding="0.5rem 1rem 1rem 1rem" gap="1rem">
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Stack
              flexDirection={isMobile ? 'column' : 'row'}
              gap="0.5rem"
              alignItems={isMobile ? 'flex-start' : 'center'}
            >
              {practiceConfig?.practiceInformation
                ?.showPracticeIntroMessage && (
                <LuxTypography>
                  {practiceConfig?.practiceInformation?.practiceIntroMessage}
                </LuxTypography>
              )}
              {/*RATINGS */}
              <Stack flexDirection={'row'} alignItems={'center'}>
                {rating &&
                  reviews &&
                  reviews.reviews &&
                  reviews.totalReviewsNumber > 0 &&
                  practiceConfig?.practiceInformation?.showRatingAndReview && (
                    <>
                      {renderStars(rating, colorConfig?.primaryColor)}
                      <LuxButton
                        variant="link"
                        onClick={() => openGoogleReviews(placeId || '')}
                      >
                        <LuxTypography variant="textXs">
                          {t('practiceCard.reviewsLink')
                            .replace('{{rating}}', rating.toString())
                            .replace(
                              '{{reviewsCounter}}',
                              reviews.totalReviewsNumber.toString(),
                            )}
                        </LuxTypography>
                      </LuxButton>
                    </>
                  )}
              </Stack>
            </Stack>
            {!isMobile && (
              <LuxButton
                onClick={() => {
                  history.push(`${Urls.APPOINTMENT_TYPE}`);
                }}
              >
                {t('bookingTitle')}
              </LuxButton>
            )}
          </Stack>
          <Stack flexDirection="column" gap="0.25rem" alignItems="flex-start">
            {!isMobile ? (
              <>
                <Stack
                  flexDirection={isMobile ? 'column' : 'row'}
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  gap={isMobile ? '0.5rem' : '2.5rem'}
                >
                  <LuxTypography
                    variant="textMdRegular"
                    color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                  >
                    {formatGeneralAddress(practiceInfo)}
                  </LuxTypography>
                  {practiceConfig?.practiceInformation?.showPracticeHours && (
                    <Stack
                      flexDirection="row"
                      gap={'0.25rem'}
                      alignContent="center"
                    >
                      {isOpen ? (
                        <LuxSelectablePill
                          selectable={false}
                          color={'success'}
                          text={t('practiceCard.open')}
                        />
                      ) : todayHours.length === 0 ? (
                        <LuxTypography>
                          {t('practiceCard.closed')}
                        </LuxTypography>
                      ) : (
                        <LuxTypography>{todayHours.join(', ')}</LuxTypography>
                      )}
                      {isOpen && closingTime && (
                        <LuxTypography alignContent="center">
                          {'-'} {t('practiceCard.closeAt')} {closingTime}
                        </LuxTypography>
                      )}
                    </Stack>
                  )}
                  <LuxButton
                    variant="link"
                    onClick={() => {
                      if (practiceInfo?.phoneNumber) {
                        window.location.href = `tel:${practiceInfo.phoneNumber}`;
                      }
                    }}
                    sx={{
                      '& .MuiTypography-root': {
                        color:
                          theme.palette.LUX.obTokens.textColorBaseSecondary,
                      },
                    }}
                  >
                    {practiceInfo?.phoneNumber || ''}
                  </LuxButton>
                  <LuxButton
                    onClick={() => {
                      if (practiceInfo?.website) {
                        const url = practiceInfo.website.startsWith('https')
                          ? practiceInfo.website
                          : `https://${practiceInfo.website}`;
                        window.open(url, '_blank');
                      }
                    }}
                    variant="link"
                    sx={{
                      '& .MuiTypography-root': {
                        color:
                          theme.palette.LUX.obTokens.textColorBaseSecondary,
                      },
                    }}
                  >
                    {practiceInfo?.website || ''}
                  </LuxButton>
                </Stack>
                <LuxButton
                  variant="link"
                  iconStart="LocationIcon"
                  size="small"
                  onClick={() => setOpenDirectionsModal(true)}
                >
                  <LuxTypography variant="textMdRegular">
                    {t('generalCta.getDirections')}
                  </LuxTypography>
                </LuxButton>
                {isFetching ? (
                  <LuxLoader />
                ) : images?.length > 0 &&
                  dataGallery?.showPracticeCardsGallery ? (
                  <Grid
                    container
                    spacing={0.5}
                    p={2}
                    width="100%"
                    padding="unset"
                  >
                    {/* immagine grande */}
                    <Grid item xs={7.2} sx={{ flexBasis: '60%' }}>
                      <FirstImage src={images[0]?.url} alt={images[0]?.alt} />
                    </Grid>

                    {/* immagini piccole */}
                    <Grid
                      item
                      container
                      spacing={0.5}
                      xs={4.8}
                      sx={{ flexBasis: '40%' }}
                    >
                      {images?.slice(1, 5).map((src, index, arr) => (
                        <Grid
                          item
                          xs={6}
                          key={index}
                          sx={{
                            borderRadius: '0.5rem',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Contenitore dell'immagine + overlay */}
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              borderRadius: '0.5rem',
                              height: '11.75rem',
                            }}
                          >
                            <RightImages
                              src={images[index]?.url}
                              alt={`Small ${images[index]?.alt}`}
                            />
                            {index === arr.length - 1 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  bgcolor: 'rgba(0,0,0,0.5)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  opacity: 1,
                                  borderRadius: '0.5rem',
                                  cursor: 'pointer',
                                }}
                                onClick={openGalleryModal}
                                onKeyDown={handleOpenGalleryKeyDown}
                                role="button"
                                tabIndex={0}
                                aria-label={String(
                                  t('practiceCard.viewAllPhotos', {
                                    n: images?.length || 0,
                                  }),
                                )}
                              >
                                <LuxSelectablePill
                                  iconStart="ImageIcon"
                                  text={t('practiceCard.viewAllPhotos', {
                                    n: images?.length || 0,
                                  })}
                                  customColor={
                                    theme.palette.LUX.tokens
                                      .backgroundsGenericBackground2
                                  }
                                  selectable={false}
                                  customBorderColor={
                                    theme.palette.LUX.tokens.whiteWhiteA100
                                  }
                                />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                ) : (
                  <></>
                )}
              </>
            ) : (
              <>
                <Stack
                  flexDirection="column"
                  gap={'0.25rem'}
                  alignItems="flex-start"
                >
                  {practiceConfig?.practiceInformation?.showPracticeHours && (
                    <Stack flexDirection="row" gap={'0.25rem'}>
                      {isOpen ? (
                        <LuxSelectablePill
                          selectable={false}
                          color={'success'}
                          text={t('practiceCard.open')}
                        />
                      ) : todayHours.length === 0 ? (
                        <LuxTypography>
                          {t('practiceCard.closed')}
                        </LuxTypography>
                      ) : (
                        <LuxTypography>{todayHours.join(', ')}</LuxTypography>
                      )}
                      {isOpen && closingTime && (
                        <LuxTypography alignContent="center">
                          {'-'} {t('practiceCard.closeAt')} {closingTime}
                        </LuxTypography>
                      )}
                    </Stack>
                  )}
                  <Stack flexDirection="column" gap="0.25rem">
                    <LuxTypography
                      variant="textMdRegular"
                      color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                    >
                      {practiceInfo?.address.line},
                    </LuxTypography>
                    <LuxTypography
                      color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                    >
                      {practiceInfo?.address.city},{' '}
                      {practiceInfo?.address.postalCode}
                    </LuxTypography>
                  </Stack>
                  <LuxButton
                    variant="link"
                    onClick={() => {
                      if (practiceInfo?.phoneNumber) {
                        window.location.href = `tel:${practiceInfo.phoneNumber}`;
                      }
                    }}
                    sx={{
                      '& .MuiTypography-root': {
                        color:
                          theme.palette.LUX.obTokens.textColorBaseSecondary,
                      },
                    }}
                  >
                    {practiceInfo?.phoneNumber || ''}
                  </LuxButton>
                  <LuxTypography
                    underline
                    color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                  >
                    {practiceInfo?.website || ''}
                  </LuxTypography>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </StepContainer>
      <GalleryImagesModal
        onClose={() => setOpenModal(false)}
        open={openModal}
        images={images || []}
      />
      <DirectionsModal
        open={openDirectionsModal}
        onClose={() => setOpenDirectionsModal(false)}
      />
    </>
  );
}
